import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Cpu, ShieldCheck } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [needMfa, setNeedMfa] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await login(email, password, needMfa ? totp : undefined);
      if (res.mfaRequired) {
        setNeedMfa(true);
        setError("");
      } else {
        nav("/profils");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
    setBusy(false);
  };

  return (
    <div className="grid-bg flex min-h-[calc(100vh-4rem)] items-center justify-center px-4">
      <div className="w-full max-w-sm rounded-xl border border-[#1E2638] bg-[#0F141C] p-7 animate-fade-up">
        <div className="mb-6 flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground"><Cpu className="h-5 w-5" /></span>
          <h1 className="font-head text-xl font-extrabold">Connexion</h1>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>E-mail</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required disabled={needMfa} data-testid="login-email-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Mot de passe</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required disabled={needMfa} data-testid="login-password-input" />
          </div>
          {needMfa && (
            <div className="space-y-1.5 rounded-lg border border-primary/40 bg-[#080B10] p-3">
              <Label className="flex items-center gap-1.5 text-primary"><ShieldCheck className="h-4 w-4" /> Code d'authentification</Label>
              <p className="text-xs text-muted-foreground">Saisissez le code à 6 chiffres de votre application d'authentification, ou un code de secours.</p>
              <Input
                inputMode="numeric"
                maxLength={6}
                autoFocus
                value={totp}
                onChange={(e) => setTotp(e.target.value.replace(/\D/g, ""))}
                placeholder="123456"
                data-testid="login-totp-input"
                className="font-mono2 tracking-[0.3em]"
              />
            </div>
          )}
          {error && <p className="text-sm text-red-400" data-testid="login-error">{error}</p>}
          <Button type="submit" className="w-full" disabled={busy} data-testid="login-submit-button">
            {busy ? "…" : needMfa ? "Vérifier le code" : "Se connecter"}
          </Button>
        </form>
        <p className="mt-4 text-center text-sm text-muted-foreground">
          Pas de compte ? <Link to="/register" className="text-primary hover:underline" data-testid="goto-register">Créer un compte</Link>
        </p>
      </div>
    </div>
  );
}
